<script setup lang="ts">
import { computed, ref } from 'vue'
import { devResetConversation } from '../../api/devreset'
import { useInbox } from '../../stores/inbox'
import LibDialog from '../library/LibDialog.vue'
import Icon from '../ui/Icon.vue'

/**
 * "ล้างแชทนี้ (ทดสอบ)" — owner 27 ก.ย. 69: "เพิ่มปุ่มเครีย์ chat สำหรับ dev mode เพราะฉันอยากเริ่มคุยใหม่ซ้ำๆ".
 * A quiet link at the bottom of the side panel's customer tab (the chat header wraps to 3 lines at 1280/1440); shown only when the server says so
 * (ConversationDetail.dev_resettable: owner/head, a test-account 1:1 chat — api/app/devreset.py).
 * An in-app confirm (never window.confirm); on success the chat leaves every screen (WS conversation.removed).
 * Owner 17 ต.ค. 69 (lean panel): it now opens from the chat header's "…" menu — `headless` hides the link and the
 * menu calls `show()`.
 */
const props = defineProps<{ convId: string; name: string; channel: string; headless?: boolean }>()
const store = useInbox()
const open = ref(false)
const busy = ref(false)
const error = ref<string | null>(null)

const CUSTOMER_SIDE: Record<string, string> = {
  line: 'ลูกค้าในแอป LINE ยังเห็นข้อความเดิม',
  fb: 'ลูกค้าในแอป Messenger ยังเห็นข้อความเดิม',
  ig: 'ลูกค้าในแอป Instagram ยังเห็นข้อความเดิม',
  web: 'หน้าแชทบนเว็บของลูกค้ายังแสดงข้อความเดิมจนกว่าจะโหลดหน้าใหม่',
}
const customerSide = computed(() => CUSTOMER_SIDE[props.channel] ?? 'ลูกค้าในแอป LINE/Messenger ยังเห็นข้อความเดิม')

function show(): void {
  error.value = null
  open.value = true
}
function close(): void {
  if (!busy.value) open.value = false
}
async function confirm(): Promise<void> {
  if (busy.value) return
  busy.value = true
  error.value = null
  try {
    await devResetConversation(props.convId)
    busy.value = false
    open.value = false
    store.removeConversation(props.convId) // at once; the socket event that follows finds nothing left
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e)
  } finally {
    busy.value = false
  }
}
defineExpose({ show })
</script>

<template>
  <button
    v-if="!headless"
    type="button"
    class="dr-b"
    title="ลบข้อความและข้อมูลของแชทนี้ในระบบ เพื่อเริ่มคุยใหม่ (เฉพาะบัญชีทดสอบ)"
    @click="show"
  >
    <Icon name="refresh" /><span>ล้างแชทนี้ (ทดสอบ)</span>
  </button>
  <LibDialog
    v-if="open"
    title="ล้างแชทนี้ (ทดสอบ)"
    :sub="`${name} · บัญชีทดสอบ`"
    :width="460"
    :close-on-backdrop="!busy"
    @close="close"
  >
    <ul class="dr-list">
      <li><Icon name="trash" /><span>ลบข้อความและข้อมูลของแชทนี้ในระบบทั้งหมด เริ่มคุยใหม่ได้ทันที</span></li>
      <li><Icon name="eye" /><span>{{ customerSide }}</span></li>
      <li><Icon name="receipt" /><span>ออเดอร์ที่ลงใน POS beta แล้วยังอยู่ใน POS</span></li>
      <li class="dr-warn"><Icon name="alert" /><span>ย้อนกลับไม่ได้</span></li>
    </ul>
    <p v-if="error" class="dr-err" role="alert"><Icon name="alertCircle" /><span>{{ error }}</span></p>
    <template #footer>
      <span class="grow"></span>
      <button type="button" class="btn" autofocus :disabled="busy" @click="close">ยกเลิก</button>
      <button type="button" class="btn dr-go" :disabled="busy" @click="confirm">
        <Icon name="trash" />{{ busy ? 'กำลังล้าง…' : 'ล้างแชท' }}
      </button>
    </template>
  </LibDialog>
</template>

<style scoped>
.dr-b {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px;
  margin: 0 -4px;
  border-radius: 6px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
  white-space: nowrap;
}
.dr-b:hover {
  color: var(--crit);
  background: var(--crits);
}
.dr-b :deep(.ic) {
  width: 13px;
  height: 13px;
}
.dr-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.dr-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13.5px;
  line-height: 21px;
  color: var(--ink);
}
.dr-list :deep(.ic) {
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: 2.5px;
  color: var(--mut);
}
.dr-list li.dr-warn {
  color: var(--crit);
  font-weight: 600;
}
.dr-list li.dr-warn :deep(.ic) {
  color: var(--crit);
}
.dr-err {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--crits);
  color: var(--crit);
  font-size: 13px;
  line-height: 20px;
}
.dr-err :deep(.ic) {
  flex: none;
  width: 15px;
  height: 15px;
  margin-top: 2.5px;
}
.btn:disabled {
  opacity: 0.6;
}
.dr-go {
  background: var(--crit);
  border-color: var(--crit);
  color: #fff;
  font-weight: 600;
}
.dr-go:hover:not(:disabled) {
  background: #a82217;
}
.dr-go :deep(.ic) {
  color: #fff;
}
</style>
